import useLogOut from '@src/hooks/useLogOut';
import {
  useDatoPerson,
  useUserIdentity,
  useVideoCreatorStore,
} from '@src/stores-v2';
import SwitchUserIcon from '@src/svgs/SwitchUserIcon';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import OrganizationAvatarTooltip, {
  MenuIcon,
  MenuLabel,
  MenuRow,
} from './OrganizationAvatarTooltip';
import OrganizationAvatarWithTooltip from './OrganizationAvatarWithTootlip';
import OrganizationSwitcher from './OrganizationSwitcher';

import type { Showcase } from '../types.ts/story';

type OrganizationSwitcherAvatarProps = {
  logoSize?: number;
  compact?: boolean;
};

function MultiOrganizationAvatar({
  logoSize = 34,
  compact = false,
}: OrganizationSwitcherAvatarProps) {
  const videoCreator = useVideoCreatorStore();
  const datoPerson = useDatoPerson();
  const { logOut } = useLogOut();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdentity = useUserIdentity();

  const album = datoPerson.selectedOrganization || videoCreator.organization;
  const username = datoPerson.person?.name;
  const handleSelect = useCallback(
    (org: Showcase) => {
      if (searchParams.get('showcase') !== org.slug) {
        searchParams.set('showcase', org.slug);
        searchParams.delete('videoId');
        searchParams.delete('storyId');
        // Folder ids belong to the previous workspace.
        searchParams.delete('folder');
        setSearchParams(searchParams);
      }
      datoPerson.selectedOrganization = org;
      datoPerson.searchTerm = '';
    },
    [searchParams, setSearchParams, datoPerson],
  );
  const handleSearch = useCallback(
    (searchTerm: string) => {
      datoPerson.searchTerm = searchTerm;
    },
    [datoPerson],
  );
  const email = userIdentity.currentIdentity?.email;

  // useCallback to trigger an update when search term changes
  const renderReactTooltip = useCallback(
    () => (
      <OrganizationAvatarTooltip
        onLogOut={logOut}
        email={email}
        compact={compact}
        album={album}
        username={username}
        topChildren={
          <>
            <MenuRow $static>
              <MenuIcon>
                <SwitchUserIcon fillColor="currentColor" />
              </MenuIcon>
              <MenuLabel>Switch Projects</MenuLabel>
            </MenuRow>
            <OrganizationSwitcher
              organizations={datoPerson.organizationsFiltered}
              onSelect={handleSelect}
              onSearch={handleSearch}
              selectedOrganization={album!}
              initialSearchTerm={datoPerson.term}
            />
          </>
        }
      />
    ),
    [
      datoPerson.organizationsFiltered,
      datoPerson.term,
      album,
      email,
      logOut,
      handleSelect,
      handleSearch,
      compact,
      username,
    ],
  );

  const hoverLabel = compact
    ? [album?.title, username].filter(Boolean).join(' · ')
    : undefined;

  return (
    <OrganizationAvatarWithTooltip
      album={album}
      username={username}
      logoSize={logoSize}
      compact={compact}
      hoverLabel={hoverLabel}
      email={email}
      renderReactTooltip={renderReactTooltip}
    />
  );
}

export default observer(MultiOrganizationAvatar);
